import { configTimeoutMs } from './constants.js';
import { configUrl, origin } from './snippet.js';
import { runtime } from './runtime.js';

/**
 * Fetches the widget's public branding config; resolves null on any error or after a short timeout
 * so a slow or failing endpoint can never delay or break the launcher.
 * @returns {Promise<object|null>}
 */
export function fetchConfig() {
    if (typeof fetch !== 'function') {
        return Promise.resolve(null);
    }

    const timeout = new Promise(function (resolve) {
        setTimeout(resolve, configTimeoutMs, null);
    });

    const request = fetch(configUrl)
        .then(function (response) {
            // Did somebody else answer? Then the snippet names a host that only forwards to the real
            // one, and this loader would spend the whole page load talking past it — see reportWrongHost.
            const answeredBy = originOf(response.url);
            if (answeredBy && answeredBy !== origin) {
                reportWrongHost(answeredBy);
                return null;
            }

            return response.ok ? response.json() : null;
        })
        .catch(function () {
            return null;
        });

    return Promise.race([request, timeout]);
}

/** The origin of a URL, or null where there is none to read (empty/opaque response URLs). */
function originOf(url) {
    try {
        return url ? new URL(url).origin : null;
    } catch {
        return null;
    }
}

/**
 * The snippet points at a host that merely redirects to the widget server. Everything this file does
 * after loading is addressed to the origin of its own script tag: the iframe (whose document then
 * commits on the OTHER host, so the ready handshake is dropped and the panel remounts itself every
 * few seconds), the analytics beacon (a POST, which a redirect cannot carry across hosts — it is
 * answered with 404) and every message in both directions. None of that is repairable from here: a
 * redirect does not change what `script.src` reads back. So the widget stays off the page and says
 * why — a broken chat that reloads under the visitor's hands is worse than no chat, and the fix is
 * one attribute in the snippet.
 * @param {string} answeredBy - The origin that actually answered the config request
 */
function reportWrongHost(answeredBy) {
    runtime.wrongHost = true;
    console.error('[leadwin-agent] The snippet loads this script from ' + origin + ', but the widget'
        + ' server is ' + answeredBy + '. Change the script src to '
        + answeredBy + '/widget/leadwin-agent.js.');
}
